import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { EnvironmentService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/environment_connect";
import {
  EnvironmentStatus,
  ListEnvironmentStatusTransitionsRequest,
} from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/environment_pb";
import type { Timestamp } from "@bufbuild/protobuf";
import { createConnectClient } from "server/connect";
import { Environment } from "components/Environment/utils/types";
import { getEnvironmentStatusLabelFromStatus } from "components/Environment/utils/environtmentUtils";
import { Spinner } from "@kui-react/spinner";

const environmentServiceClient = createConnectClient(EnvironmentService);

const getTimestampMillis = (timestamp?: Timestamp): number => {
  if (!timestamp) return 0;
  return Number(timestamp.seconds) * 1000 + timestamp.nanos / 1000000;
};

const formatTransitionTime = (timestamp?: Timestamp): string => {
  const millis = getTimestampMillis(timestamp);
  if (!millis) return "Unknown time";
  return new Date(millis).toLocaleString();
};

const formatElapsedInState = (
  enteredAt?: Timestamp,
  nextStateEnteredAt?: Timestamp,
): string => {
  const startMillis = getTimestampMillis(enteredAt);
  const endMillis = nextStateEnteredAt
    ? getTimestampMillis(nextStateEnteredAt)
    : Date.now();
  if (!startMillis || !endMillis) return "-";

  const durationMillis = Math.max(0, endMillis - startMillis);

  const totalSeconds = Math.round(durationMillis / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const TransitionStatusBadge: React.FC<{
  status: EnvironmentStatus;
}> = ({ status }) => {
  return (
    <span className="font-medium text-primary">
      {getEnvironmentStatusLabelFromStatus(status) ?? "Unspecified"}
    </span>
  );
};

const HistorySettings: React.FC<{ environment: Environment }> = ({
  environment,
}) => {
  const environmentId = environment.environment_id;
  const {
    data: transitions = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["environment-status-transitions", environmentId],
    queryFn: async () => {
      if (!environmentId) return [];
      const res =
        await environmentServiceClient.listEnvironmentStatusTransitions(
          new ListEnvironmentStatusTransitionsRequest({
            environmentId,
            includeDeleted: true,
          }),
        );
      return res.items;
    },
    enabled: !!environmentId,
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
    staleTime: 2000,
  });

  const sortedTransitions = useMemo(
    () =>
      [...transitions].sort(
        (a, b) =>
          getTimestampMillis(b.createTime) - getTimestampMillis(a.createTime),
      ),
    [transitions],
  );

  if (!environmentId) {
    return (
      <div className="text-sm text-secondary">
        Status history is not available for this environment.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-md border border-red-900/40 bg-red-950/20 px-4 py-3 text-sm text-red-200">
        Failed to load status history.
      </div>
    );
  }

  if (sortedTransitions.length === 0) {
    return (
      <div className="border-y border-zinc-800 px-4 py-6 text-sm text-secondary">
        No status transitions have been recorded yet.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border-y border-zinc-800">
      <div className="grid min-w-[620px] grid-cols-[minmax(160px,1fr)_minmax(190px,1fr)_minmax(120px,0.7fr)] border-b border-zinc-800 px-4 py-3 text-xs font-semibold uppercase text-secondary">
        <div>State</div>
        <div>Entered</div>
        <div>Elapsed</div>
      </div>
      <div className="divide-y divide-zinc-800">
        {sortedTransitions.map((transition, index) => (
          <div
            key={transition.transitionId}
            className="grid min-w-[620px] grid-cols-[minmax(160px,1fr)_minmax(190px,1fr)_minmax(120px,0.7fr)] gap-3 px-4 py-3 text-sm"
          >
            <div>
              <TransitionStatusBadge status={transition.toStatus} />
            </div>
            <div className="text-secondary">
              {formatTransitionTime(transition.createTime)}
            </div>
            <div className="truncate text-secondary">
              {formatElapsedInState(
                transition.createTime,
                sortedTransitions[index - 1]?.createTime,
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HistorySettings;
